import React, { useState } from 'react';
import styles from './ExpectedValueCalculator.module.css';
import { clearFormatting, handleBlurCurrency, handleBlurPercentage, handleFocus } from '../../../utils/calc-utils';

const ExpectedValueCalculator = () => {
  const [calculationType, setCalculationType] = useState('Standard');
  const [fields, setFields] = useState({
    evStake: '',
    evBackOdds: '',
    evTrueOdds: '',
    evOverround: '0.00%',
    indComboOdds: '',
    indAmountNeeded: '',
    indOdds: ['', ''],
    indOverround: '0.00%',
  });
  const [result, setResult] = useState('');
  const [isResultVisible, setIsResultVisible] = useState(false);

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setFields((prevFields) => ({ ...prevFields, [id]: value }));
  };

  const handleAddIndOdds = () => {
    setFields((prevFields) => ({
      ...prevFields,
      indOdds: [...prevFields.indOdds, ''],
    }));
  };

  const handleRemoveIndOdds = () => {
    if (fields.indOdds.length > 2) {
      setFields((prevFields) => ({
        ...prevFields,
        indOdds: prevFields.indOdds.slice(0, -1),
      }));
    }
  };

  const handleCalculateEV = () => {
    if (calculationType === 'Standard') {
      calculateStandardEV();
    } else if (calculationType === 'Independent') {
      calculateIndependentEV();
    }
    setIsResultVisible(true); // Show the result box
  };

  const calculateStandardEV = () => {
    const { evStake, evBackOdds, evTrueOdds, evOverround } = fields;
    if (!evStake || !evBackOdds || !evTrueOdds || !evOverround) {
      setResult('Finish entering all your bet details before calculating');
      return;
    }

    const stake = clearFormatting(evStake);
    const backOdds = parseFloat(evBackOdds);
    const trueOdds = parseFloat(evTrueOdds);
    const assumedOverround = clearFormatting(evOverround);

    const profit = stake * (backOdds - 1);
    const loss = -stake;
    const winProbability = 1 / (trueOdds * (1 + assumedOverround / 100));
    const lossProbability = 1 - winProbability;
    const ev = profit * winProbability + loss * lossProbability;
    const percentEV = (ev / stake) * 100;

    setResult(
      `Expected Value (EV) is equal to <span class="${ev >= 0 ? styles.profitTextGreen : styles.profitTextRed}">${ev >= 0 ? '' : '-'}$${Math.abs(ev).toFixed(2)}</span>, which represents a <span class="${percentEV >= 0 ? styles.profitTextGreen : styles.profitTextRed}">${percentEV.toFixed(2)}%</span> return.`
    );
  };

  const calculateIndependentEV = () => {
    const { indComboOdds, indAmountNeeded, indOverround, indOdds } = fields;
    if (!indComboOdds || !indAmountNeeded || !indOverround || indOdds.some((odds) => !odds)) {
      setResult('Finish entering all your bet details before calculating');
      return;
    }

    // Validate X value is an integer
    if (!Number.isInteger(parseFloat(indAmountNeeded))) {
      setResult('The X value must be a whole number');
      return;
    }

    const comboOdds = parseFloat(indComboOdds);
    const amountNeeded = parseFloat(indAmountNeeded);
    const assumedOverround = parseFloat(indOverround.replace(/[$,%]/g, ''));
    const odds = indOdds.map((odds) => parseFloat(odds) * (1 + assumedOverround / 100));

    if (odds.length < amountNeeded) {
      setResult(`Error in your input, it is impossible to require ${amountNeeded} out of ${odds.length} outcomes to win.`);
      return;
    }

    let totalProbability = 0;
    for (let k = amountNeeded; k <= odds.length; k++) {
      const combinations = getCombinations(odds.length, k);

      for (const comb of combinations) {
        let combProbability = 1;

        // Calculate probability for the chosen combination
        for (const idx of comb) {
          combProbability *= 1 / odds[idx];
        }

        // Calculate probability for events not in the combination
        for (let idx = 0; idx < odds.length; idx++) {
          if (!comb.includes(idx)) {
            combProbability *= 1 - 1 / odds[idx];
          }
        }

        // Accumulate the total probability
        totalProbability += combProbability;
      }
    }

    const trueOdds = 1 / totalProbability;
    const ev = ((comboOdds - trueOdds) / trueOdds) * 100;

    setResult(
      `The expected value for at least ${amountNeeded} out of these ${odds.length} independent events occurring is <span class="${ev >= 0 ? styles.profitTextGreen : styles.profitTextRed}">${ev.toFixed(2)}%</span>. The True Odds of the event occurring is ${trueOdds.toFixed(2)}`
    );
  };

  const getCombinations = (n, k) => {
    const result = [];
    const combine = (start, choose, prefix) => {
      if (choose === 0) {
        result.push(prefix);
        return;
      }
      for (let i = start; i <= n - choose; i++) {
        combine(i + 1, choose - 1, prefix.concat(i));
      }
    };
    combine(0, k, []);
    return result;
  };

  return (
    <div className={styles.evCalculator}>
      <h1 className={styles.calculatorTitle}>Expected Value Calculator</h1>
      <div className={styles.containerWrapper}>
        <div className={styles.container}>
          <div className={styles.sectionHeader}>
          </div>
          <div className={styles.box}>
            <span className={styles.label}>Calculation Type:</span>
            <select
              className={styles.dropdown}
              id="calculationType"
              value={calculationType}
              onChange={(e) => setCalculationType(e.target.value)}
            >
              <option value="Standard">Standard</option>
              <option value="Independent">At least X of N</option>
            </select>
          </div>
          {calculationType === 'Standard' ? (
            <div className={styles.standardEvFields}>
              <div className={styles.box}>
                <span className={styles.label}>Stake($):</span>
                <input
                  className={`${styles.input} ${styles.stake}`}
                  type="text"
                  id="evStake"
                  value={fields.evStake}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurCurrency(e, setFields)}
                  onFocus={(e) => handleFocus(e, setFields)}
                  placeholder="Enter stake"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>Back Odds:</span>
                <input
                  className={`${styles.input} ${styles.backOdds}`}
                  type="number"
                  id="evBackOdds"
                  value={fields.evBackOdds}
                  onChange={handleInputChange}
                  placeholder="Enter back odds"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>True Odds:</span>
                <input
                  className={`${styles.input} ${styles.trueOdds}`}
                  type="number"
                  id="evTrueOdds"
                  value={fields.evTrueOdds}
                  onChange={handleInputChange}
                  placeholder="Enter true odds"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>Assumed Overround (%):</span>
                <input
                  className={`${styles.input} ${styles.overround}`}
                  type="text"
                  id="evOverround"
                  value={fields.evOverround}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurPercentage(e, setFields)}
                  onFocus={(e) => handleFocus(e, setFields)}
                  placeholder="Enter assumed overround"
                />
              </div>
            </div>
          ) : (
            <div className={styles.independentEvFields}>
              <div className={styles.box}>
                <span className={styles.label}>Combo Odds:</span>
                <input
                  className={`${styles.input} ${styles.stake}`}
                  type="number"
                  id="indComboOdds"
                  value={fields.indComboOdds}
                  onChange={handleInputChange}
                  placeholder="Enter combo odds"
                />
              </div>
              <div className={styles.box}>
                <span className={styles.label}>X Value:</span>
                <input
                  className={`${styles.input} ${styles.stake}`}
                  type="number"
                  id="indAmountNeeded"
                  value={fields.indAmountNeeded}
                  onChange={handleInputChange}
                  placeholder="Enter X value"
                />
              </div>
              {fields.indOdds.map((odds, index) => (
                <div className={styles.box} key={index}>
                  <span className={styles.label}>Odds {index + 1}:</span>
                  <input
                    className={`${styles.input} ${styles.odds}`}
                    type="number"
                    value={odds}
                    onChange={(e) => {
                      const newOdds = [...fields.indOdds];
                      newOdds[index] = e.target.value;
                      setFields((prevFields) => ({
                        ...prevFields,
                        indOdds: newOdds,
                      }));
                    }}
                    placeholder={`Enter odds ${index + 1}`}
                  />
                </div>
              ))}
              <div className={styles.box}>
                <span className={styles.label}>Assumed Overround (%):</span>
                <input
                  className={`${styles.input} ${styles.overround}`}
                  type="text"
                  id="indOverround"
                  value={fields.indOverround}
                  onChange={handleInputChange}
                  onBlur={(e) => handleBlurPercentage(e, setFields)}
                  onFocus={(e) => handleFocus(e, setFields)}
                  placeholder="Enter assumed overround"
                />
              </div>
              <div className={`${styles.box} ${styles.buttonContainer}`}>
                <button
                  type="button"
                  onClick={handleAddIndOdds}
                  className={`${styles.greyAccentButton} ${fields.indOdds.length <= 2 ? styles.fullWidth : ''}`}
                >
                  Add Another Option
                </button>
                {fields.indOdds.length > 2 && (
                  <button
                    className={`${styles.greyAccentButton} ${styles.buttonBuffer}`}
                    type="button"
                    onClick={handleRemoveIndOdds}
                  >
                    Remove Last Option
                  </button>
                )}
              </div>
            </div>
          )}
          <button onClick={handleCalculateEV}>Calculate</button>
          <div className={`${styles.result} ${isResultVisible ? styles.resultVisible : ''}`} id="resultEV" dangerouslySetInnerHTML={{ __html: result }}>
          </div>
        </div>
        <div className={styles[`howToUse${calculationType}`]}>
          <h2>How to Use This Calculator</h2>
          <p>
            The Expected Value (EV) Calculator helps you calculate the potential
            profitability of your bets. There are two types of calculations:
          </p>
          <h3>Which mode of the calculator to use?</h3>
          <p>
              <strong>Standard:</strong> Is used to calculate the expected value of
              a single bet using back odds, true odds, and an optional overround
              percentage. An example could be an odds boost of 2.00 to 2.50 the
              true odds would be 2 and back odds would be 2.50, if using the
              bookie odds for true odds enter an appropriate overround, for example 5%.
          </p>
          <p>
              <strong>At least X of N:</strong> This mode is used to calculate the 
              expected value when you need a minimum of X out of N outcomes to succeed 
              in order to win your bet. It’s commonly used for special promotions and combo offers. 
              For instance, if a bookmaker offers a promotion where you win if either Player 
              A or Player B scores a goal, the X value would be set to 1, and the N value would be set to 2.

          </p>
          <h3>Explanation of the individual inputs</h3>
          {calculationType === 'Standard' ? (
            <>
              <p>
                1. <strong>Stake:</strong> Enter the amount of money you are betting.
              </p>
              <p>
                2. <strong>Back Odds:</strong> Enter the odds provided to you by the bookmaker.
              </p>
              <p>
                3. <strong>True Odds:</strong> Enter what you believe to be the true odds.
              </p>
              <p>
                4. <strong>Assumed Overround (%):</strong> If you think the true odds you entered still have a bit of house edge in them enter what percent you believe that to be.
              </p>
            </>
          ) : (
            <>
              <p>
                1. <strong>Combo Odds:</strong> Enter the odds the bookmaker is
                offering for this promotion in the "Combo Odds" field.
              </p>
              <p>
                2. <strong>X Value:</strong> Enter the minimum number of bets
                you need to win in the "X Value" field.
              </p>
              <p>
                3. <strong>Odds 1:</strong> Enter the odds for the first
                individual outcome in the "Odds 1" field.
              </p>
              <p>
                4. <strong>Odds 2:</strong> Enter the odds for the second
                individual outcome in the "Odds 2" field. Use the "Add Another
                Option" button to add more individual bets as needed.
              </p>
              <p>
                5. <strong>Assumed Overround (%):</strong> Enter the overround
                percentage, if any, in the "Assumed Overround (%)" field.
              </p>
              <p>
                <strong>How it works:</strong> This calculator determines the
                expected value of a series of bets where you need to win at
                least X out of N bets. It takes into account the combined odds,
                individual odds, and assumed overround to assess the potential
                profitability.
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpectedValueCalculator;